// Взгляд игры на машину: какую её точку держать в середине экрана и насколько приближать.
//
// Обычный вид один на всю игру: машина стоит так, что видны бонусный ряд, поле 3x3 и рамка
// вокруг них, см. scenes/gameScene.ts. Бонус Steam Spin играет выше поля, на мини-слоте,
// поэтому на время бонуса взгляд подходит к нему ближе: машина едет вниз, а мини-слот
// вырастает на весь экран, см. GDD, раздел 5.3.
//
// Двигается узел машины целиком, вместе с дымом и всем, что на ней лежит, и вместе с ним —
// фон. Приближение фон повторяет один к одному: игра нарисована почти реалистично, и машина,
// растущая на неподвижной картинке, читалась бы как прыжок к игроку, а не как подход камеры.
// Сдвиг фон повторяет лишь отчасти, как дальний план.
//
// Кнопки в ход взгляда не входят: они стоят на экране, а не в мире игры.
//
// Точка взгляда задаётся по вертикали: машина у́же экрана, и вбок ей ехать некуда.

import type { Container } from 'pixi.js';

import { smoothStep } from './scene';

/** Сколько взгляд идёт от одного вида к другому. */
const MOVE_SECONDS: number = 0.6;

/**
 * Насколько фон повторяет сдвиг взгляда.
 *
 * Повторив его целиком, фон уехал бы вниз вместе с машиной и открыл бы пустоту над собой.
 * Дальний план и не должен ехать наравне с ближним: доля взята с запасом от того, насколько
 * фон вырастает от приближения, поэтому за край экрана он не уходит ни на одном кадре хода.
 */
const BACKDROP_SHIFT: number = 0.4;

/** Как картинка барабанов лежит в макете при обычном виде. */
export interface MachineView {
  /** Точка картинки, которая стоит в середине свободного места. */
  readonly anchorY: number;

  /** Масштаб картинки в единицах макета. */
  readonly scale: number;

  /** Середина свободного места по вертикали в координатах макета. */
  readonly centerY: number;
}

/** Ход взгляда: откуда, куда и сколько уже пройдено. */
interface Move {
  readonly fromY: number;
  readonly fromZoom: number;
  readonly toY: number;
  readonly toZoom: number;
  passed: number;
}

/** Взгляд игры на машину. */
export class Camera {
  readonly #view: Container;
  readonly #backdrop: Container;
  readonly #machine: MachineView;

  /** Точка картинки в середине свободного места на этот миг. */
  #y: number;

  /** Приближение на этот миг. Единица — обычный вид. */
  #zoom = 1;

  #move: Move | null = null;
  #onArrived: (() => void) | null = null;

  /**
   * @param view Узел машины. Его и двигает взгляд.
   * @param backdrop Узел фона. Он повторяет приближение целиком, а сдвиг — лишь отчасти.
   */
  constructor(view: Container, backdrop: Container, machine: MachineView) {
    this.#view = view;
    this.#backdrop = backdrop;
    this.#machine = machine;
    this.#y = machine.anchorY;

    this.#place();
  }

  /**
   * Ведёт взгляд к заданной точке картинки и приближению, ждёт, пока он доедет.
   *
   * Новый ход обрывает прежний: держать два разом взгляд не умеет, а брошенное ожидание
   * не кончилось бы никогда.
   *
   * @param y Точка картинки барабанов по вертикали.
   * @param zoom Приближение. Единица — обычный вид.
   */
  moveTo(y: number, zoom: number): Promise<void> {
    this.#arrive();

    if (y === this.#y && zoom === this.#zoom)
      return Promise.resolve();

    this.#move = { fromY: this.#y, fromZoom: this.#zoom, toY: y, toZoom: zoom, passed: 0 };

    return new Promise<void>((resolve) => {
      this.#onArrived = resolve;
    });
  }

  /** Возвращает взгляд к обычному виду и ждёт, пока он доедет. */
  moveHome(): Promise<void> {
    return this.moveTo(this.#machine.anchorY, 1);
  }

  /** Ставит взгляд на место разом: игрок торопит показ. */
  skip(): void {
    const move: Move | null = this.#move;
    if (move === null)
      return;

    this.#y = move.toY;
    this.#zoom = move.toZoom;
    this.#move = null;

    this.#place();
    this.#arrive();
  }

  update(seconds: number): void {
    const move: Move | null = this.#move;
    if (move === null)
      return;

    move.passed += seconds;

    const passed: number = Math.min(move.passed / MOVE_SECONDS, 1);
    const step: number = smoothStep(passed);

    this.#y = move.fromY + (move.toY - move.fromY) * step;
    this.#zoom = move.fromZoom + (move.toZoom - move.fromZoom) * step;
    this.#place();

    if (passed < 1)
      return;

    this.#move = null;
    this.#arrive();
  }

  /**
   * Ставит узлы машины и фона под нынешний взгляд.
   *
   * Узлы лежат в макете уже с обычным видом, поэтому взгляд правит их поверх: приближение
   * идёт множителем, а сдвиг возвращает точку взгляда в середину свободного места.
   * При обычном виде правка выходит пустой, и узлы стоят там, где их положила сцена.
   *
   * Фон растёт наравне с машиной, а едет за ней лишь отчасти: он дальний план и центр экрана
   * покидать не должен.
   */
  #place(): void {
    const { anchorY, scale, centerY } = this.#machine;
    const y: number = (this.#y - anchorY) * scale + centerY;
    const shift: number = centerY - this.#zoom * y;

    this.#view.scale.set(this.#zoom);
    this.#view.position.set(0, shift);

    this.#backdrop.scale.set(this.#zoom);
    this.#backdrop.position.set(0, shift * BACKDROP_SHIFT);
  }

  /** Будит того, кто ждёт взгляд. Второй раз будить некого. */
  #arrive(): void {
    const arrived: (() => void) | null = this.#onArrived;

    this.#onArrived = null;
    arrived?.();
  }
}
